// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { useTranslations } from "next-intl";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  VN_PHONE_REGEX,
  businessTypeOptions,
  type Org,
  type OrgLegalType,
} from "@chotca/config";
import {
  Badge,
  Button,
  Card,
  FormField,
  ImageGalleryEditor,
  Input,
  LogoUploader,
  Select,
  Skeleton,
  useToast,
  type GalleryImage,
} from "@chotca/ui";
import { useState } from "react";
import { ErrorState } from "@/components/error-state";
import { BranchesCard } from "@/components/employer/branches-card";
import { useMe } from "@chotca/api-client";
import {
  useOrg,
  useUpdateOrg,
  useUploadOrgGalleryImage,
  useUploadOrgLogo,
} from "@chotca/api-client";

const orgInfoSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập tên doanh nghiệp")
    .max(120, "Tên doanh nghiệp tối đa 120 ký tự"),
  businessType: z
    .string()
    .trim()
    .min(1, "Vui lòng chọn loại hình kinh doanh")
    .max(60, "Loại hình tối đa 60 ký tự"),
  taxCode: z.string().trim().max(20, "Mã số thuế tối đa 20 ký tự"),
  contactName: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập người liên hệ")
    .max(120, "Người liên hệ tối đa 120 ký tự"),
  contactPhone: z
    .string()
    .trim()
    .regex(
      VN_PHONE_REGEX,
      "SĐT không hợp lệ. Nhập số Việt Nam bắt đầu bằng 0 hoặc +84.",
    ),
});

type OrgInfoValues = z.infer<typeof orgInfoSchema>;

export function OrgSettingsClient() {
  const t = useTranslations("orgSettings");
  const me = useMe();
  const membership = me.data?.memberships[0];
  const orgId = membership?.orgId;
  const isOwner = membership?.role === "OWNER";
  const org = useOrg(orgId);
  const update = useUpdateOrg(orgId ?? "");
  const { toast } = useToast();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<OrgInfoValues>({
    resolver: zodResolver(orgInfoSchema),
    mode: "onTouched",
    // `values` keeps the form in sync once the org query resolves; until then
    // it's undefined and the form holds empty strings.
    values: org.data
      ? {
          name: org.data.name,
          businessType: org.data.businessType,
          taxCode: org.data.taxCode ?? "",
          contactName: org.data.contactName,
          contactPhone: org.data.contactPhone,
        }
      : undefined,
  });

  const save = handleSubmit(async (values) => {
    try {
      // Chỉ cập nhật thông tin liên hệ. Định danh pháp lý (name, businessType,
      // taxCode) chỉ đổi được qua luồng KYC do admin duyệt.
      await update.mutateAsync({
        contactName: values.contactName,
        contactPhone: values.contactPhone,
      });
      toast({ tone: "success", title: t("saveSuccess") });
      reset(values); // clears dirty state, keeps the saved values
    } catch {
      toast({ tone: "error", title: t("saveError") });
    }
  });

  if (org.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (org.isError || !org.data) {
    return <ErrorState onRetry={() => org.refetch()} />;
  }

  const o = org.data;

  const canSubmitKyc =
    isOwner &&
    (o.kycStatus === "NONE" ||
      o.kycStatus === "REJECTED" ||
      o.kycStatus === "EXPIRED");

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-display-lg font-extrabold text-ink">
          {t("title")}
        </h1>
        <div className="flex items-center gap-3">
          <KycBadge status={o.kycStatus} />
          {canSubmitKyc ? (
            <Link
              to="/org/kyc"
              className="rounded-full bg-lime px-4 py-1.5 text-sm font-semibold text-ink hover:bg-lime/85"
            >
              {t("kycSubmitCta")}
            </Link>
          ) : null}
        </div>
      </header>
      {o.kycStatus === "REJECTED" && o.kycRejectReason ? (
        <p className="text-body-sm text-danger">
          {t("kycRejectReason")}: {o.kycRejectReason}
        </p>
      ) : null}

      <nav className="flex flex-wrap gap-2">
        <Tab href="/org" label={t("tabInfo")} active />
        <Tab href="/org/activity" label={t("tabActivity")} />
      </nav>

      <Card className="p-6">
        <h2 className="text-title-lg font-bold text-ink">{t("infoTitle")}</h2>
        <form onSubmit={save} noValidate>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {/* Định danh pháp lý — chỉ đổi được qua luồng KYC do admin duyệt,
                không cho sửa trực tiếp ở đây. */}
            <FormField label={t("fieldName")} error={errors.name?.message}>
              <Input disabled invalid={!!errors.name} {...register("name")} />
            </FormField>
            <FormField
              label={t("fieldBusinessType")}
              error={errors.businessType?.message}
            >
              <Controller
                control={control}
                name="businessType"
                render={({ field }) => (
                  <Select
                    disabled
                    invalid={!!errors.businessType}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Chọn loại hình kinh doanh"
                    options={businessTypeOptions(field.value)}
                  />
                )}
              />
            </FormField>
            <FormField
              label={t("fieldTaxCode")}
              error={errors.taxCode?.message}
            >
              <Input
                disabled
                invalid={!!errors.taxCode}
                {...register("taxCode")}
              />
            </FormField>
            <FormField
              label={t("fieldContactPhone")}
              error={errors.contactPhone?.message}
            >
              <Input
                disabled={!isOwner}
                invalid={!!errors.contactPhone}
                {...register("contactPhone")}
              />
            </FormField>
            <FormField
              label={t("fieldContactName")}
              error={errors.contactName?.message}
            >
              <Input
                disabled={!isOwner}
                invalid={!!errors.contactName}
                {...register("contactName")}
              />
            </FormField>
          </div>
          {isOwner ? (
            <div className="mt-6 flex justify-end">
              <Button
                type="submit"
                disabled={!isDirty || update.isPending}
                loading={update.isPending}
              >
                {t("saveSuccess")}
              </Button>
            </div>
          ) : null}
        </form>
      </Card>

      {orgId ? <OrgVerifyLevelCard org={o} /> : null}

      {orgId ? <OrgLegalCard org={o} canEdit={isOwner} /> : null}

      {orgId ? (
        <OrgLogoCard
          orgId={orgId}
          initialUrl={o.logoPublicUrl}
          canEdit={isOwner}
        />
      ) : null}

      {orgId ? (
        <OrgGalleryCard
          orgId={orgId}
          initial={toGalleryImages(o.galleryImageKeys, o.galleryPublicUrls)}
          canEdit={isOwner}
        />
      ) : null}

      {orgId ? (
        <>
          {o.addressNeedsWardUpdate ? <WardUpdateBanner /> : null}
          <BranchesCard orgId={orgId} canEdit={isOwner} />
        </>
      ) : null}
    </div>
  );
}

// Nudges employers whose branches were saved before the 2025 ward migration
// (wardCode is null) to re-open the address and pick the new phường/xã. Clears
// automatically once every active branch has a ward.
function WardUpdateBanner() {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 p-3.5">
      <MapPin size={18} className="mt-0.5 shrink-0 text-warning" />
      <div className="text-sm">
        <p className="font-semibold text-ink">
          Cập nhật địa chỉ theo phường / xã mới
        </p>
        <p className="mt-0.5 text-ash">
          Địa chỉ của bạn đang theo địa giới cũ (quận / huyện). Từ 01/07/2025
          Việt Nam chuyển sang phường / xã. Hãy mở từng chi nhánh bên dưới và
          chọn lại địa chỉ để cập nhật.
        </p>
      </div>
    </div>
  );
}

// Zip the persisted keys with their resolved public URLs (parallel arrays,
// same order) into the {key,url} pairs the editor works with.
function toGalleryImages(keys: string[], urls: string[]): GalleryImage[] {
  return keys.map((key, i) => ({ key, url: urls[i] ?? "" }));
}

function OrgLogoCard({
  orgId,
  initialUrl,
  canEdit,
}: {
  orgId: string;
  initialUrl: string | null;
  canEdit: boolean;
}) {
  const t = useTranslations("orgSettings");
  const { toast } = useToast();
  const update = useUpdateOrg(orgId);
  const upload = useUploadOrgLogo(orgId);
  const [url, setUrl] = useState<string | null>(initialUrl);

  return (
    <Card className="p-6">
      <h2 className="text-title-lg font-bold text-ink">{t("logoTitle")}</h2>
      <div className="mt-5">
        <LogoUploader
          url={url}
          disabled={!canEdit}
          onUpload={async (file, onProgress) => {
            const r = await upload.mutateAsync({ file, onProgress });
            return { key: r.key, url: r.publicUrl };
          }}
          onChange={async (next) => {
            const prev = url;
            setUrl(next.url);
            try {
              await update.mutateAsync({ logoImageKey: next.key });
            } catch {
              setUrl(prev);
              toast({ tone: "error", title: t("saveError") });
            }
          }}
          onRemove={async () => {
            const prev = url;
            setUrl(null);
            try {
              await update.mutateAsync({ logoImageKey: null });
            } catch {
              setUrl(prev);
              toast({ tone: "error", title: t("saveError") });
            }
          }}
          labels={{
            upload: t("logoUpload"),
            change: t("logoChange"),
            remove: t("logoRemove"),
            view: t("logoView"),
            close: t("galleryClose"),
            uploadError: t("logoUploadError"),
            hint: t("logoHint"),
          }}
        />
      </div>
    </Card>
  );
}

function OrgGalleryCard({
  orgId,
  initial,
  canEdit,
}: {
  orgId: string;
  initial: GalleryImage[];
  canEdit: boolean;
}) {
  const t = useTranslations("orgSettings");
  const { toast } = useToast();
  const update = useUpdateOrg(orgId);
  const upload = useUploadOrgGalleryImage(orgId);
  const [images, setImages] = useState<GalleryImage[]>(initial);

  // Each add / remove / reorder persists the whole ordered set immediately —
  // matches the rest of the app's "no explicit save for media" pattern.
  async function persist(next: GalleryImage[]) {
    const prev = images;
    setImages(next);
    try {
      await update.mutateAsync({ galleryImageKeys: next.map((i) => i.key) });
    } catch {
      setImages(prev);
      toast({ tone: "error", title: t("saveError") });
    }
  }

  return (
    <Card className="p-6">
      <h2 className="text-title-lg font-bold text-ink">{t("galleryTitle")}</h2>
      <div className="mt-5">
        <ImageGalleryEditor
          images={images}
          disabled={!canEdit}
          onUpload={async (file, onProgress) => {
            const r = await upload.mutateAsync({ file, onProgress });
            return { key: r.key, url: r.publicUrl };
          }}
          onChange={(next) => void persist(next)}
          labels={{
            add: t("galleryAdd"),
            counter: (count, max) => t("galleryCounter", { count, max }),
            remove: t("galleryRemove"),
            moveBack: t("galleryMoveBack"),
            moveForward: t("galleryMoveForward"),
            uploadError: t("galleryUploadError"),
            cover: t("galleryCover"),
            view: t("galleryView"),
            close: t("galleryClose"),
            prev: t("galleryPrev"),
            next: t("galleryNext"),
            hint: t("galleryHint"),
          }}
        />
      </div>
    </Card>
  );
}

function Tab({
  href,
  label,
  active,
}: {
  href: string;
  label: string;
  active?: boolean;
}) {
  return (
    <Link
      to={href}
      className={
        active
          ? "rounded-full bg-ink px-4 py-1.5 text-sm font-semibold text-paper"
          : "rounded-full border border-smoke px-4 py-1.5 text-sm font-semibold text-ink hover:bg-mist"
      }
    >
      {label}
    </Link>
  );
}

const LEGAL_TYPE_LABEL: Record<OrgLegalType, string> = {
  INDIVIDUAL: "Cá nhân",
  HOUSEHOLD: "Hộ kinh doanh",
  COMPANY: "Công ty",
};

const VERIFY_LEVEL_LABEL: Record<string, string> = {
  L0_NEW: "Chưa xác minh",
  L1_CONTACT: "Đã xác minh liên hệ",
  L2_DOCUMENT: "Đã xác minh giấy tờ",
  L3_TRUSTED: "Nhà tuyển dụng uy tín",
};

// Verification level + what's still missing to reach the next one (SCRUM-74).
// Read-only; the level is computed server-side. Missing-condition hints are a
// best-effort client view derived from the org's current data.
function OrgVerifyLevelCard({ org }: { org: Org }) {
  const level = org.verifyLevel ?? "L0_NEW";
  const tone =
    level === "L3_TRUSTED" || level === "L2_DOCUMENT" ? "lime-soft" : "mist";

  // Conditions still missing for the next level (coarse — the server is source
  // of truth). We surface the actionable ones the employer can fix.
  const missing: string[] = [];
  if (level === "L0_NEW" || level === "L1_CONTACT") {
    if (org.legalType === "INDIVIDUAL") {
      missing.push("Người đại diện xác minh CCCD (đạt mức U2) để lên L2.");
    } else if (org.kycStatus !== "APPROVED") {
      missing.push("Nộp và chờ duyệt hồ sơ giấy phép kinh doanh để lên L2.");
    }
  }

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-title-lg font-bold text-ink">Mức xác thực</h2>
        <Badge tone={tone as "lime-soft" | "mist"}>
          {VERIFY_LEVEL_LABEL[level]}
        </Badge>
      </div>
      <p className="text-body-sm text-ink/70">
        Mức xác thực hiển thị cho ứng viên. Càng cao càng đáng tin, không ảnh
        hưởng tới việc đăng tin.
      </p>
      {missing.length > 0 ? (
        <div className="mt-3 space-y-1 rounded-xl bg-mist px-3 py-2.5">
          <p className="text-body-sm font-semibold text-ink">
            Để lên mức tiếp:
          </p>
          {missing.map((m) => (
            <p key={m} className="text-body-sm text-ink/70">
              • {m}
            </p>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

// "Thông tin pháp lý" block on /org (SCRUM-73), separate from the outlet-info
// card above. INDIVIDUAL has no legal entity, so it only shows a hint. HOUSEHOLD
// / COMPANY show + let the owner edit the registration fields.
function OrgLegalCard({ org, canEdit }: { org: Org; canEdit: boolean }) {
  const { toast } = useToast();
  const update = useUpdateOrg(org.id);
  const [legalName, setLegalName] = useState(org.legalName ?? "");
  const [legalRegNo, setLegalRegNo] = useState(org.legalRegNo ?? "");
  const [legalAddress, setLegalAddress] = useState(org.legalAddress ?? "");

  const isIndividual = org.legalType === "INDIVIDUAL";

  const save = async () => {
    try {
      await update.mutateAsync({
        legalName: legalName.trim() || null,
        legalRegNo: legalRegNo.trim() || null,
        legalAddress: legalAddress.trim() || null,
      });
      toast({ tone: "success", title: "Đã lưu thông tin pháp lý" });
    } catch (e) {
      toast({
        tone: "error",
        title: e instanceof Error ? e.message : "Lưu thất bại",
      });
    }
  };

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-title-lg font-bold text-ink">Thông tin pháp lý</h2>
        <Badge tone="mist">{LEGAL_TYPE_LABEL[org.legalType]}</Badge>
      </div>

      {isIndividual ? (
        <p className="text-body-sm text-ink/70">
          Loại hình cá nhân không cần giấy tờ pháp nhân. Việc xác minh nằm ở
          phần định danh cá nhân của người đại diện.
        </p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Tên đăng ký kinh doanh">
              <Input
                disabled={!canEdit}
                maxLength={200}
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
              />
            </FormField>
            <FormField label="Số ĐKKD / MST">
              <Input
                disabled={!canEdit}
                maxLength={50}
                value={legalRegNo}
                onChange={(e) => setLegalRegNo(e.target.value)}
              />
            </FormField>
          </div>
          <FormField label="Địa chỉ đăng ký kinh doanh">
            <Input
              disabled={!canEdit}
              maxLength={300}
              value={legalAddress}
              onChange={(e) => setLegalAddress(e.target.value)}
            />
          </FormField>
          {canEdit ? (
            <div className="flex justify-end">
              <Button onClick={save} loading={update.isPending} type="button">
                Lưu
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function KycBadge({ status }: { status: string }) {
  const t = useTranslations("orgSettings");
  type Tone = "smoke" | "sun-soft" | "lime-soft" | "danger-soft";
  const map: Record<string, { tone: Tone; label: string }> = {
    NONE: { tone: "smoke", label: t("kycStatusNone") },
    PENDING: { tone: "sun-soft", label: t("kycStatusPending") },
    APPROVED: { tone: "lime-soft", label: t("kycStatusApproved") },
    REJECTED: { tone: "danger-soft", label: t("kycStatusRejected") },
    EXPIRED: { tone: "sun-soft", label: t("kycStatusExpired") },
  };
  const cur = map[status] ?? map.NONE!;
  return <Badge tone={cur.tone}>{cur.label}</Badge>;
}
